<template>
  <div class="metrics-page">
    <div class="metrics-container">
      <!-- Header -->
      <div class="metrics-header">
        <div class="header-content">
          <h1 class="page-title">Dashboard DAU / MAU</h1>
          <p class="page-subtitle">
            Recherches lancées (dossiers créés) — périmètre Premium + Active
            <span v-if="perimeterCount"> · {{ perimeterCount }} utilisateurs éligibles</span>
          </p>
        </div>
        <router-link :to="{ name: 'adminDashboard' }" class="back-button">
          ← Retour au dashboard
        </router-link>
      </div>

      <!-- Contrôles -->
      <div class="controls">
        <div class="presets">
          <button v-for="p in presets" :key="p.key"
                  :class="['preset-btn', { active: activePreset === p.key }]"
                  @click="applyPreset(p.key)">{{ p.label }}</button>
        </div>
        <div class="control-right">
          <label class="date-field">Du <input type="date" v-model="from" @change="onCustomDate" /></label>
          <label class="date-field">Au <input type="date" v-model="to" @change="onCustomDate" /></label>
          <label class="toggle">
            <input type="checkbox" v-model="excludeNonWorking" />
            Jours ouvrés uniquement <span class="muted">(exclut week-ends + fériés FR)</span>
          </label>
        </div>
      </div>

      <div v-if="error" class="error-banner">{{ error }}</div>
      <div v-if="loading" class="loading">Chargement…</div>

      <template v-if="!loading && series.length">
        <!-- KPIs -->
        <div class="kpi-row">
          <div class="kpi-card">
            <div class="kpi-label">MAU (30 j glissant)</div>
            <div class="kpi-value">{{ mau30 }}</div>
            <div class="kpi-sub">au {{ fmtDate(to) }}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">Stickiness DAU / MAU</div>
            <div class="kpi-value accent">{{ stickiness.toFixed(1) }} %</div>
            <div class="kpi-sub">{{ excludeNonWorking ? 'jours ouvrés' : 'tous jours' }}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">DAU moyen</div>
            <div class="kpi-value">{{ avgDau.toFixed(0) }}</div>
            <div class="kpi-sub">{{ excludeNonWorking ? 'jours ouvrés' : 'tous jours' }}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">Actifs sur la période</div>
            <div class="kpi-value">{{ rangeMau }}</div>
            <div class="kpi-sub">distinct {{ fmtDate(from) }} → {{ fmtDate(to) }}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">Pic DAU</div>
            <div class="kpi-value">{{ peak.value }}</div>
            <div class="kpi-sub">le {{ fmtDate(peak.date) }}</div>
          </div>
          <div class="kpi-card">
            <div class="kpi-label">Recherches</div>
            <div class="kpi-value">{{ totalSearches }}</div>
            <div class="kpi-sub">sur la période</div>
          </div>
        </div>

        <!-- Graphe -->
        <div class="chart-card">
          <canvas ref="chart"></canvas>
        </div>
        <p class="footnote">
          DAU = utilisateurs distincts ayant lancé ≥1 recherche le jour donné · MAU 30 j =
          distinct sur [J-29, J] · Stickiness = DAU moyen ÷ MAU 30j <em>moyen sur la période
          affichée</em> (numérateur et dénominateur sur la même fenêtre). La courbe de stickiness
          par jour peut grimper haut au tout début (base très petite = effet « petit
          dénominateur »). Survole un point pour la valeur exacte d'un jour. Source : public.dossiers
          (TZ Europe/Paris).
        </p>
      </template>
    </div>
  </div>
</template>

<script>
import { supabase } from "@/supabase/supabaseClient";
import Chart from "chart.js/auto";

const API_BASE = "https://api.query-juriste.com";

export default {
  name: "MetricsPage",
  data() {
    return {
      from: "",
      to: "",
      series: [],
      rangeMau: 0,
      perimeterCount: 0,
      loading: false,
      error: null,
      adminPassword: null,
      excludeNonWorking: false,
      activePreset: "90d",
      chart: null,
      presets: [
        { key: "30d", label: "30 j" },
        { key: "90d", label: "90 j" },
        { key: "6m", label: "6 mois" },
        { key: "12m", label: "12 mois" },
        { key: "cur", label: "Mois en cours" },
        { key: "prev", label: "Mois préc." },
        { key: "all", label: "Tout" },
      ],
    };
  },
  computed: {
    filteredDays() {
      if (!this.excludeNonWorking) return this.series;
      return this.series.filter((s) => s.is_weekday && !s.is_holiday);
    },
    avgDau() {
      const d = this.filteredDays;
      if (!d.length) return 0;
      return d.reduce((a, s) => a + s.dau, 0) / d.length;
    },
    mau30() {
      // Base active actuelle (30j glissant au dernier jour) — pour la carte "MAU".
      return this.series.length ? this.series[this.series.length - 1].mau30 : 0;
    },
    avgMau30() {
      // Moyenne du MAU 30j sur les jours affichés (filtrés) = dénominateur de stickiness,
      // pour que numérateur et dénominateur portent sur la MÊME période.
      const d = this.filteredDays;
      if (!d.length) return 0;
      return d.reduce((a, s) => a + s.mau30, 0) / d.length;
    },
    stickiness() {
      return this.avgMau30 ? (this.avgDau / this.avgMau30) * 100 : 0;
    },
    totalSearches() {
      return this.series.reduce((a, s) => a + s.searches, 0);
    },
    peak() {
      const d = this.filteredDays;
      if (!d.length) return { value: 0, date: this.to };
      return d.reduce((m, s) => (s.dau > m.value ? { value: s.dau, date: s.date } : m),
        { value: 0, date: this.to });
    },
  },
  watch: {
    excludeNonWorking() {
      this.renderChart();
    },
  },
  mounted() {
    this.applyPreset("90d");
  },
  beforeUnmount() {
    if (this.chart) this.chart.destroy();
  },
  methods: {
    fmtDate(d) {
      if (!d) return "";
      const [y, m, day] = d.split("-");
      return `${day}/${m}/${y}`;
    },
    promptForPassword() {
      if (!this.adminPassword) {
        const pwd = prompt("Mot de passe administrateur requis:");
        if (!pwd) throw new Error("Mot de passe requis");
        this.adminPassword = pwd;
      }
      return this.adminPassword;
    },
    isoDay(date) {
      return date.toISOString().slice(0, 10);
    },
    applyPreset(key) {
      this.activePreset = key;
      const today = new Date();
      const to = new Date(today);
      let from = new Date(today);
      if (key === "30d") from.setDate(to.getDate() - 29);
      else if (key === "90d") from.setDate(to.getDate() - 89);
      else if (key === "6m") from.setMonth(to.getMonth() - 6);
      else if (key === "12m") from.setMonth(to.getMonth() - 12);
      else if (key === "cur") from = new Date(today.getFullYear(), today.getMonth(), 1);
      else if (key === "prev") {
        from = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        to.setTime(new Date(today.getFullYear(), today.getMonth(), 0).getTime());
      } else if (key === "all") from = new Date(2025, 3, 1); // avril 2025
      this.from = this.isoDay(from);
      this.to = this.isoDay(to);
      this.fetchData();
    },
    onCustomDate() {
      if (this.from && this.to && this.from <= this.to) {
        this.activePreset = null;
        this.fetchData();
      }
    },
    async fetchData() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await supabase.auth.getSession();
        const jwt = data?.session?.access_token;
        if (!jwt) throw new Error("Session expirée");
        const pwd = this.promptForPassword();
        const url = `${API_BASE}/copilot/admin/metrics/dau-mau?from=${this.from}&to=${this.to}`;
        const resp = await fetch(url, {
          headers: { Authorization: `Bearer ${jwt}`, "X-Admin-Password": pwd },
        });
        if (!resp.ok) {
          if (resp.status === 403) this.adminPassword = null;
          const e = await resp.json().catch(() => ({}));
          throw new Error(e.error || `Erreur ${resp.status}`);
        }
        const json = await resp.json();
        this.series = json.series || [];
        this.rangeMau = json.range_mau || 0;
        this.perimeterCount = json.perimeter_count || 0;
        this.$nextTick(() => this.renderChart());
      } catch (err) {
        this.error = err.message;
      } finally {
        this.loading = false;
      }
    },
    renderChart() {
      if (!this.$refs.chart || !this.series.length) return;
      const ctx = this.$refs.chart.getContext("2d");
      if (this.chart) this.chart.destroy();
      const labels = this.series.map((s) => s.date);
      const dau = this.series.map((s) => s.dau);
      const mau = this.series.map((s) => s.mau30);
      const stick = this.series.map((s) => (s.mau30 ? (s.dau / s.mau30) * 100 : null));
      // grise les barres week-end/fériés quand le toggle est actif
      const barColors = this.series.map((s) =>
        this.excludeNonWorking && (!s.is_weekday || s.is_holiday)
          ? "rgba(160,174,192,0.35)"
          : "rgba(46,139,87,0.75)"
      );
      this.chart = new Chart(ctx, {
        data: {
          labels,
          datasets: [
            { type: "bar", label: "DAU", data: dau, yAxisID: "y",
              backgroundColor: barColors, order: 3 },
            { type: "line", label: "MAU 30 j glissant", data: mau, yAxisID: "y",
              borderColor: "#1A56DB", borderWidth: 2, pointRadius: 0, tension: 0.3, order: 1 },
            { type: "line", label: "Stickiness DAU/MAU (%)", data: stick, yAxisID: "y1",
              borderColor: "#c0392b", borderWidth: 1.5, borderDash: [4, 3],
              pointRadius: 0, tension: 0.3, order: 2 },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: { mode: "index", intersect: false },
          plugins: {
            legend: { position: "top" },
            tooltip: {
              callbacks: {
                afterBody: (items) => {
                  const i = items[0].dataIndex;
                  const s = this.series[i];
                  const tag = s.is_holiday ? "férié" : s.is_weekday ? "ouvré" : "week-end";
                  return `(${tag} · ${s.searches} recherches)`;
                },
              },
            },
          },
          scales: {
            y: { beginAtZero: true, title: { display: true, text: "Utilisateurs" },
                 ticks: { precision: 0 } },
            y1: { beginAtZero: true, position: "right", title: { display: true, text: "Stickiness %" },
                  grid: { drawOnChartArea: false } },
            x: { ticks: { maxTicksLimit: 16, autoSkip: true }, grid: { display: false } },
          },
        },
      });
    },
  },
};
</script>

<style scoped>
.metrics-page {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  min-height: 100vh;
  padding: 2rem 1rem;
}

.metrics-container {
  max-width: 1200px;
  margin: 0 auto;
}

.metrics-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;
  color: white;
}

.header-content {
  flex: 1;
}

.page-title {
  font-size: 2rem;
  font-weight: 700;
  color: white;
  margin: 0 0 0.4rem 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.page-subtitle {
  color: white;
  opacity: 0.85;
  font-size: 0.95rem;
  margin: 0;
}

.back-button {
  background: rgba(255, 255, 255, 0.15);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 8px;
  padding: 8px 16px;
  text-decoration: none;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  white-space: nowrap;
}

.back-button:hover {
  background: rgba(255, 255, 255, 0.25);
}

.controls {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: space-between;
  align-items: center;
  background: rgba(255, 255, 255, 0.95);
  padding: 1.25rem 1.5rem;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  margin-bottom: 1.5rem;
}

.presets {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.preset-btn {
  padding: 6px 12px;
  border: 1px solid #e2e8f0;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  font-size: 13px;
  color: #4a5568;
  transition: all 0.2s ease;
}

.preset-btn:hover {
  border-color: #667eea;
  color: #667eea;
}

.preset-btn.active {
  background: linear-gradient(135deg, #667eea, #764ba2);
  color: white;
  border-color: transparent;
}

.control-right {
  display: flex;
  gap: 14px;
  align-items: center;
  flex-wrap: wrap;
}

.date-field {
  font-size: 13px;
  color: #4a5568;
}

.date-field input {
  margin-left: 4px;
  padding: 4px 6px;
  border: 1px solid #e2e8f0;
  border-radius: 6px;
}

.toggle {
  font-size: 13px;
  color: #4a5568;
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
}

.toggle .muted {
  color: #a0aec0;
}

.kpi-row {
  display: grid;
  grid-template-columns: repeat(6, 1fr);
  gap: 12px;
  margin-bottom: 1.5rem;
}

.kpi-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  padding: 1.25rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.kpi-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
}

.kpi-label {
  font-size: 12px;
  color: #718096;
  font-weight: 500;
}

.kpi-value {
  font-size: 24px;
  font-weight: 700;
  color: #2d3748;
  margin: 4px 0 2px;
}

.kpi-value.accent {
  color: #667eea;
}

.kpi-sub {
  font-size: 11px;
  color: #a0aec0;
}

.chart-card {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 16px;
  padding: 1.5rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  height: 460px;
}

.footnote {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 10px;
  font-style: italic;
}

.error-banner {
  background: rgba(254, 242, 242, 0.9);
  color: #b91c1c;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.loading {
  color: white;
  padding: 40px;
  text-align: center;
}

@media (max-width: 900px) {
  .kpi-row { grid-template-columns: repeat(2, 1fr); }
  .metrics-header { flex-direction: column; gap: 1rem; align-items: flex-start; }
}
</style>
